import { AxiosResponse } from 'axios';
import noop from 'lodash/noop';
import ReconnectingWebSocket from 'reconnecting-websocket';
import { Task } from 'redux-saga';
import { SagaIterator } from 'redux-saga';
import { EventChannel, eventChannel } from 'redux-saga';
import { call, cancel, delay, fork, put, race, select, take } from 'redux-saga/effects';

import { httpGET, refreshAccessToken } from '../../services/http.ts';
import { WEBSOCKET_URL } from '../../services/websocket.ts';
import { Lamp } from '../../types/lamp.ts';
import { NotificationResponse } from '../../types/notification.ts';
import { JobStatus, Project, Version } from '../../types/project.ts';
import { User } from '../../types/user.ts';
import {
  AutoPositioningStatus,
  BakeStatus,
  CalculationResultStatus,
  CatalogueSyncFinishedEvent,
  CatalogueSyncStatus,
  EndAutoPositioningEvent,
  EndCalculationEvent,
  EndLampSimilarityEvent,
  EndRenderBakingEvent,
  EndReportImagesEvent,
  JobStartedEvent,
  LampSimilarityStatus,
  ReportImagesStatus,
  WebSocketEventType,
  WebSocketMessage,
} from '../../types/webSocket.types.ts';
import { isJobCompleted, isJobFailed, isJobInProgress } from '../../utils/jobStatus.ts';
import { playNotificationSound } from '../../utils/notificationSound.ts';
import { notificationsReducer } from '../reducers/notifications.reducer.ts';
import { toastReducer } from '../reducers/toast.reducer.ts';
import { userReducer } from '../reducers/user.reducer.ts';
import {
  addLamps,
  downloadReportImages,
  loadBakedModelFile,
  loadLampSimilarityDetails,
  loadMeasurementFile,
  loadProject,
  removeLamp,
  resetFileProgress,
  setLampSimilarityDetails,
  updateActiveVersion,
} from '../reducers/workspace.reducer.ts';
import { RootState } from '../store.ts';
import { hasUnsavedWorkspaceEdits } from './workspace.saga.ts';

type JobLike = { id: number; jobStatus: JobStatus } | null | undefined;

// Internal synthetic event type for reconnect signal
const WS_RECONNECTED = '__WS_RECONNECTED__' as const;
type ReconnectedMessage = { type: typeof WS_RECONNECTED; data: '' };
type ChannelMessage = WebSocketMessage | ReconnectedMessage;

type WebSocketChannelHandle = {
  channel: EventChannel<ChannelMessage>;
  forceReconnect: () => void;
};

/** Prevents duplicate completion side-effects when catch-up and WS race. */
const claimedJobCompletions = new Set<string>();

export function clearClaimedJobCompletions() {
  claimedJobCompletions.clear();
}

function claimJobCompletion(job: JobLike): boolean {
  if (!job?.id) return false;
  const key = `${job.id}:${job.jobStatus}`;
  if (claimedJobCompletions.has(key)) return false;
  claimedJobCompletions.add(key);
  return true;
}

function isJobTerminal(job: JobLike): boolean {
  return isJobCompleted(job) || isJobFailed(job) || job?.jobStatus === JobStatus.CANCELED;
}

function versionHasActiveJob(version: Version | null | undefined): boolean {
  if (!version) return false;
  return (
    isJobInProgress(version.calculationJob) ||
    isJobInProgress(version.renderBakingJob) ||
    isJobInProgress(version.autopositioningJob) ||
    isJobInProgress(version.reportImagesJob) ||
    isJobInProgress(version.lampSimilarityJob)
  );
}

/** Merge server-owned job/result fields without replacing local meshes. */
function mergeVersionJobFields(local: Version, server: Version): Version {
  return {
    ...local,
    calculationJob: server.calculationJob,
    reportImagesJob: server.reportImagesJob,
    renderBakingJob: server.renderBakingJob,
    autopositioningJob: server.autopositioningJob,
    lampSimilarityJob: server.lampSimilarityJob,
    lampSimilarityResult: server.lampSimilarityResult,
    measurementFileId: server.measurementFileId,
    optimisticLockKey: server.optimisticLockKey,
    updatedAt: server.updatedAt,
    bakedModelUrl: server.bakedModelUrl ?? local.bakedModelUrl,
    reportImageUrls: server.reportImageUrls ?? local.reportImageUrls,
    // Keep version.lights aligned with server results (lumenOutput, ies, etc.) so
    // workspace lamp sync doesn't leave a permanent unsaved-diff vs the snapshot.
    lights: server.lights ?? local.lights,
  };
}

function isWorkspacePageWithProjectAndVersion(projectId: number, versionId: number): boolean {
  const { pathname, search } = new URL(window.location.href);
  const params = new URLSearchParams(search);

  const urlProjectId = params.get('projectId');
  const urlVersionId = params.get('versionId');

  return pathname === '/workspace' && urlProjectId === projectId.toString() && urlVersionId === versionId.toString();
}

function* isActiveWorkspaceContext(projectId: number, versionId: number): Generator<unknown, boolean, unknown> {
  const project = (yield select((state: RootState) => state.workspace.project)) as Project | null;
  const matchesStore = project?.id === projectId && project?.activeVersionId === versionId;
  if (!matchesStore) return false;
  return isWorkspacePageWithProjectAndVersion(projectId, versionId);
}

const SUBSCRIBE_TO_WEBSOCKET_ACTION = 'ws/subscribeToWS';
const UNSUBSCRIBE_FROM_WEBSOCKET_ACTION = 'ws/unsubscribeFromWS';

export const subscribeToWebSocket = () => ({
  type: SUBSCRIBE_TO_WEBSOCKET_ACTION,
});

export const unsubscribeFromWebSocket = () => ({
  type: UNSUBSCRIBE_FROM_WEBSOCKET_ACTION,
});

const { showToast } = toastReducer.actions;
const { loadNotifications } = notificationsReducer.actions;
const { setUser, setUserLoadingComplete } = userReducer.actions;
const HEARTBEAT_TIMEOUT_MS = 75_000;
const RECOVERY_REQUEST_TIMEOUT_MS = 10_000;
const JOB_CATCH_UP_POLL_MS = 5_000;
const JOB_CATCH_UP_IDLE_MS = 2_000;

function createWebSocketChannel(): WebSocketChannelHandle {
  let webSocket: ReconnectingWebSocket | null = null;

  const channel = eventChannel<ChannelMessage>((emitter) => {
    webSocket = new ReconnectingWebSocket(WEBSOCKET_URL);

    let isFirstOpen = true;

    webSocket.onopen = () => {
      if (isFirstOpen) {
        // Don't fire syncAppState on the initial connection, only on reconnects
        isFirstOpen = false;
        return;
      }
      // Emit a synthetic event so the saga can run syncAppState on reconnect
      emitter({ type: WS_RECONNECTED, data: '' });
    };

    webSocket.onmessage = (event: MessageEvent) => {
      try {
        const message = JSON.parse(event.data) as { id: number; type: string; data: unknown };
        const eventType = message.type as WebSocketEventType;
        const data = typeof message.data === 'string' ? message.data : JSON.stringify(message.data);
        emitter({ type: eventType, data });
      } catch (e) {
        console.warn('Failed to parse WebSocket message', e);
      }
    };

    webSocket.onerror = (e) => {
      // Log the error but do NOT close — ReconnectingWebSocket will retry automatically
      console.warn('WebSocket error', e);
    };

    // Do NOT call emitter(END) on close — let ReconnectingWebSocket handle reconnection
    webSocket.onclose = () => {
      console.info('WebSocket closed; ReconnectingWebSocket will attempt to reconnect');
    };

    // Cleanup: only called when the saga channel is explicitly closed (e.g. unsubscribe)
    return () => {
      webSocket?.close();
      webSocket = null;
    };
  });

  return {
    channel,
    forceReconnect: () => {
      webSocket?.reconnect();
    },
  };
}

function* ensureFreshAccessToken(): Generator<unknown, boolean, unknown> {
  try {
    yield call(refreshAccessToken);
    return true;
  } catch {
    return false;
  }
}

function lightsToWorkspaceLamps(version: Version) {
  return version.lights.map((light) => ({
    uuid: light.uuid,
    name: light.name,
    position: [light.positionX || 0, light.positionY || 0, light.positionZ || 0] as [number, number, number],
    distanceToGround: light.distanceToGround,
    // Omit null so UI totals don't treat null as 0
    ...(typeof light.lumenOutput === 'number' ? { lumenOutput: light.lumenOutput } : {}),
    iesFileId: light.iesFileId,
    productId: light.productId,
    variantId: light.variantId,
    seriesId: light.seriesId,
    manufacturerId: light.manufacturerId,
    rotationRadians: light.rotationRadians,
    marketMatchGroup: light.marketMatchGroup,
  }));
}

function* replaceWorkspaceLampsFromVersion(version: Version): Generator<unknown, void, unknown> {
  const existingLamps = (yield select((state: RootState) => state.workspace.lamps)) as Lamp[];
  for (const lamp of existingLamps) {
    yield put(removeLamp(lamp.id));
  }
  yield put(addLamps(lightsToWorkspaceLamps(version)));
}

function* applyCalculationSideEffects(
  version: Version,
  status: CalculationResultStatus,
): Generator<unknown, void, unknown> {
  const isActiveContext = (yield call(isActiveWorkspaceContext, version.projectId, version.id)) as boolean;
  if (!isActiveContext) return;

  playNotificationSound();

  if (version.measurementFileId) {
    yield put(resetFileProgress());
    yield put(loadMeasurementFile(version.measurementFileId));
  }

  if (status === CalculationResultStatus.SUCCESS) {
    // Always sync lamps from the server version — calculation writes lumenOutput/ies
    // onto lights, and workspace isn't editable while the job runs.
    yield call(replaceWorkspaceLampsFromVersion, version);
    yield put(
      showToast({
        message: 'workspace.toast.optimizationSuccess',
        type: 'success',
      }),
    );
  } else if (status === CalculationResultStatus.FAIL) {
    yield put(
      showToast({
        message: 'workspace.toast.optimizationFailed',
        type: 'error',
      }),
    );
  }
}

function* applyAutopositioningSideEffects(
  version: Version,
  status: AutoPositioningStatus,
): Generator<unknown, void, unknown> {
  const isCanceling = (yield select((state: RootState) => state.workspace.isCancelingAutoPositioning)) as boolean;
  if (isCanceling) {
    console.info('Ignoring autopositioning completion because autopositioning is being cancelled');
    return;
  }

  const isActiveContext = (yield call(isActiveWorkspaceContext, version.projectId, version.id)) as boolean;
  if (!isActiveContext) return;

  playNotificationSound();

  if (status === AutoPositioningStatus.SUCCESS) {
    yield call(replaceWorkspaceLampsFromVersion, version);
    yield put(
      showToast({
        message: 'workspace.toast.autopositioningSuccess',
        type: 'success',
      }),
    );
  } else if (status === AutoPositioningStatus.FAIL) {
    yield put(
      showToast({
        message: 'workspace.toast.autopositioningFailed',
        type: 'error',
      }),
    );
  }
}

function* applyBakeSideEffects(version: Version, status: BakeStatus): Generator<unknown, void, unknown> {
  const isActiveContext = (yield call(isActiveWorkspaceContext, version.projectId, version.id)) as boolean;
  if (!isActiveContext) return;

  playNotificationSound();

  if (status === BakeStatus.SUCCESS) {
    yield put(resetFileProgress());
    yield put(loadBakedModelFile());
    yield put(
      showToast({
        message: 'workspace.toast.renderBakingSuccess',
        type: 'success',
      }),
    );
  } else if (status === BakeStatus.FAIL) {
    yield put(
      showToast({
        message: 'workspace.toast.renderBakingFailed',
        type: 'error',
      }),
    );
  }
}

function* applyReportImagesSideEffects(
  version: Version,
  status: ReportImagesStatus,
): Generator<unknown, void, unknown> {
  const isActiveContext = (yield call(isActiveWorkspaceContext, version.projectId, version.id)) as boolean;
  if (!isActiveContext) return;

  if (status === ReportImagesStatus.SUCCESS) {
    yield put(resetFileProgress());
    yield put(downloadReportImages());
  }
}

function* applyLampSimilaritySideEffects(
  version: Version,
  status: LampSimilarityStatus,
): Generator<unknown, void, unknown> {
  const isActiveContext = (yield call(isActiveWorkspaceContext, version.projectId, version.id)) as boolean;
  if (!isActiveContext) return;

  playNotificationSound();

  if (status === LampSimilarityStatus.SUCCESS) {
    yield put(setLampSimilarityDetails([]));
    yield put(loadLampSimilarityDetails());
    yield put(
      showToast({
        message: 'workspace.toast.lampSimilaritySuccess',
        type: 'success',
      }),
    );
  } else if (status === LampSimilarityStatus.FAIL) {
    yield put(
      showToast({
        message: 'workspace.toast.lampSimilarityFailed',
        type: 'error',
      }),
    );
  }
}

function* applyMissedJobCompletions(localVersion: Version, serverVersion: Version): Generator<unknown, void, unknown> {
  if (isJobInProgress(localVersion.calculationJob)) {
    if (isJobCompleted(serverVersion.calculationJob) && claimJobCompletion(serverVersion.calculationJob)) {
      yield call(applyCalculationSideEffects, serverVersion, CalculationResultStatus.SUCCESS);
    } else if (isJobFailed(serverVersion.calculationJob) && claimJobCompletion(serverVersion.calculationJob)) {
      yield call(applyCalculationSideEffects, serverVersion, CalculationResultStatus.FAIL);
    }
  }

  if (isJobInProgress(localVersion.autopositioningJob)) {
    if (isJobCompleted(serverVersion.autopositioningJob) && claimJobCompletion(serverVersion.autopositioningJob)) {
      yield call(applyAutopositioningSideEffects, serverVersion, AutoPositioningStatus.SUCCESS);
    } else if (isJobFailed(serverVersion.autopositioningJob) && claimJobCompletion(serverVersion.autopositioningJob)) {
      yield call(applyAutopositioningSideEffects, serverVersion, AutoPositioningStatus.FAIL);
    }
  }

  if (isJobInProgress(localVersion.renderBakingJob)) {
    if (isJobCompleted(serverVersion.renderBakingJob) && claimJobCompletion(serverVersion.renderBakingJob)) {
      yield call(applyBakeSideEffects, serverVersion, BakeStatus.SUCCESS);
    } else if (isJobFailed(serverVersion.renderBakingJob) && claimJobCompletion(serverVersion.renderBakingJob)) {
      yield call(applyBakeSideEffects, serverVersion, BakeStatus.FAIL);
    }
  }

  if (isJobInProgress(localVersion.reportImagesJob)) {
    if (isJobCompleted(serverVersion.reportImagesJob) && claimJobCompletion(serverVersion.reportImagesJob)) {
      yield call(applyReportImagesSideEffects, serverVersion, ReportImagesStatus.SUCCESS);
    } else if (isJobFailed(serverVersion.reportImagesJob) && claimJobCompletion(serverVersion.reportImagesJob)) {
      yield call(applyReportImagesSideEffects, serverVersion, ReportImagesStatus.FAIL);
    }
  }

  if (isJobInProgress(localVersion.lampSimilarityJob)) {
    if (isJobCompleted(serverVersion.lampSimilarityJob) && claimJobCompletion(serverVersion.lampSimilarityJob)) {
      yield call(applyLampSimilaritySideEffects, serverVersion, LampSimilarityStatus.SUCCESS);
    } else if (isJobFailed(serverVersion.lampSimilarityJob) && claimJobCompletion(serverVersion.lampSimilarityJob)) {
      yield call(applyLampSimilaritySideEffects, serverVersion, LampSimilarityStatus.FAIL);
    }
  }
}

/**
 * Fetches the active version from the API and merges job/result fields.
 * Applies completion side-effects when a local in-progress job has finished on the server.
 */
export function* syncVersionJobState(projectId: number, versionId: number): Generator<unknown, void, unknown> {
  try {
    const { versionResponse } = (yield race({
      versionResponse: call(httpGET, `/projects/${projectId}/versions/${versionId}`),
      timeout: delay(RECOVERY_REQUEST_TIMEOUT_MS),
    })) as { versionResponse?: AxiosResponse<Version>; timeout?: true };

    if (!versionResponse?.data) {
      console.warn('Timed out or failed fetching version for job-state sync');
      return;
    }

    const serverVersion = versionResponse.data;
    const state = (yield select((s: RootState) => s)) as RootState;
    const project = state.workspace.project;
    if (!project || project.id !== projectId || project.activeVersionId !== versionId) {
      return;
    }

    const localVersion = project.versions.find((v) => v.id === versionId);
    if (!localVersion) return;

    yield put(updateActiveVersion(mergeVersionJobFields(localVersion, serverVersion)));
    yield call(applyMissedJobCompletions, localVersion, serverVersion);
  } catch (e) {
    console.warn('Failed to sync version job state', e);
  }
}

export function* syncAppState(): Generator<unknown, void, unknown> {
  try {
    const { userResponse } = (yield race({
      userResponse: call(httpGET, '/users/user'),
      timeout: delay(RECOVERY_REQUEST_TIMEOUT_MS),
    })) as { userResponse?: AxiosResponse<User>; timeout?: true };

    if (userResponse?.data) {
      yield put(setUser(userResponse.data));
    } else {
      yield put(setUserLoadingComplete());
    }

    yield put(loadNotifications());

    const currentProject = (yield select((state: RootState) => state.workspace.project)) as Project | null;
    if (!currentProject?.id || !currentProject.activeVersionId) {
      return;
    }

    // Use the same dirty detector as autosave (baseline vs last saved snapshot), not a
    // separate version-snapshot comparison that false-positives on type/norm/casing drift.
    const hasUnsavedEdits = (yield call(hasUnsavedWorkspaceEdits)) as boolean;
    if (!hasUnsavedEdits) {
      yield put(loadProject(currentProject.id));
    } else {
      // Preserve local lamp/layer edits; still reconcile job status and results.
      console.info('Merging job state after WebSocket reconnect (unsaved workspace changes present)');
      yield call(syncVersionJobState, currentProject.id, currentProject.activeVersionId);
    }
  } catch {
    yield put(setUserLoadingComplete());
  }
}

function* jobStatusCatchUpLoop(): Generator<unknown, void, unknown> {
  while (true) {
    const project = (yield select((state: RootState) => state.workspace.project)) as Project | null;
    const activeVersionId = project?.activeVersionId;
    const activeVersion = project?.versions.find((v) => v.id === activeVersionId);

    if (!project?.id || !activeVersionId || !versionHasActiveJob(activeVersion)) {
      yield delay(JOB_CATCH_UP_IDLE_MS);
      continue;
    }

    yield call(syncVersionJobState, project.id, activeVersionId);
    yield delay(JOB_CATCH_UP_POLL_MS);
  }
}

function parseWebSocketData<T>(data: string, eventType: WebSocketEventType): T | null {
  try {
    return JSON.parse(data) as T;
  } catch (e) {
    console.warn(`Failed to parse ${eventType} WebSocket event`, e);
    return null;
  }
}

function* validateProjectContext(): Generator<unknown, boolean, Project> {
  const project = yield select((state: RootState) => state.workspace.project);

  if (!project || !project.activeVersionId) {
    console.error('No project or active version found');
    return false;
  }
  return true;
}

function* getLocalVersion(versionId: number): Generator<unknown, Version | null, unknown> {
  const project = (yield select((state: RootState) => state.workspace.project)) as Project | null;
  return project?.versions.find((v) => v.id === versionId) ?? null;
}

function* handleJobStartedEvent(data: string): Generator<unknown, void, unknown> {
  const isValid = yield call(validateProjectContext);
  if (!isValid) return;

  const message = parseWebSocketData<JobStartedEvent>(data, WebSocketEventType.JOB_STARTED);
  if (!message) return;
  const updatedVersion: Version = message.version;

  yield put(updateActiveVersion(updatedVersion));
}

export function* handleEndAutoPositioningEvent(data: string): Generator<unknown, void, unknown> {
  const isCanceling = (yield select((state: RootState) => state.workspace.isCancelingAutoPositioning)) as boolean;
  if (isCanceling) {
    console.info('Ignoring END_AUTO_POSITIONING event because autopositioning is being cancelled');
    return;
  }

  const isValid = yield call(validateProjectContext);
  if (!isValid) return;

  const message = parseWebSocketData<EndAutoPositioningEvent>(data, WebSocketEventType.END_AUTO_POSITIONING);
  if (!message) return;
  const updatedVersion: Version = message.version;

  const localVersion = (yield call(getLocalVersion, updatedVersion.id)) as Version | null;
  const alreadyHandled = isJobTerminal(localVersion?.autopositioningJob);

  yield put(updateActiveVersion(updatedVersion));

  if (alreadyHandled) return;
  if (updatedVersion.autopositioningJob && !claimJobCompletion(updatedVersion.autopositioningJob)) {
    return;
  }

  yield call(applyAutopositioningSideEffects, updatedVersion, message.status);
}

export function* handleEndCalculationEvent(data: string): Generator<unknown, void, unknown> {
  const isValid = yield call(validateProjectContext);
  if (!isValid) return;

  const message = parseWebSocketData<EndCalculationEvent>(data, WebSocketEventType.END_CALCULATION);
  if (!message) return;
  const updatedVersion: Version = message.version;

  const localVersion = (yield call(getLocalVersion, updatedVersion.id)) as Version | null;
  const alreadyHandled = isJobTerminal(localVersion?.calculationJob);

  yield put(updateActiveVersion(updatedVersion));

  // If catch-up already marked the job complete but skipped lamp sync, still apply
  // photometric results from this authoritative version payload.
  if (alreadyHandled) {
    if (message.status === CalculationResultStatus.SUCCESS) {
      const isActiveContext = (yield call(
        isActiveWorkspaceContext,
        updatedVersion.projectId,
        updatedVersion.id,
      )) as boolean;
      if (isActiveContext && updatedVersion.lights?.length) {
        yield call(replaceWorkspaceLampsFromVersion, updatedVersion);
      }
    }
    return;
  }

  if (updatedVersion.calculationJob && !claimJobCompletion(updatedVersion.calculationJob)) {
    return;
  }

  yield call(applyCalculationSideEffects, updatedVersion, message.status);
}

function* handleEndRenderBakingEvent(data: string): Generator<unknown, void, unknown> {
  const isValid = yield call(validateProjectContext);
  if (!isValid) return;

  const message = parseWebSocketData<EndRenderBakingEvent>(data, WebSocketEventType.END_RENDER_BAKING);
  if (!message) return;
  const updatedVersion: Version = message.version;

  const localVersion = (yield call(getLocalVersion, updatedVersion.id)) as Version | null;
  const alreadyHandled = isJobTerminal(localVersion?.renderBakingJob);

  yield put(updateActiveVersion(updatedVersion));

  if (alreadyHandled) return;
  if (updatedVersion.renderBakingJob && !claimJobCompletion(updatedVersion.renderBakingJob)) {
    return;
  }

  yield call(applyBakeSideEffects, updatedVersion, message.status);
}

export function* handleEndReportImagesEvent(data: string): Generator<unknown, void, unknown> {
  const isValid = yield call(validateProjectContext);
  if (!isValid) return;

  const message = parseWebSocketData<EndReportImagesEvent>(data, WebSocketEventType.END_REPORT_IMAGES);
  if (!message) return;
  const updatedVersion: Version = message.version;

  const localVersion = (yield call(getLocalVersion, updatedVersion.id)) as Version | null;
  const alreadyHandled = isJobTerminal(localVersion?.reportImagesJob);

  yield put(updateActiveVersion(updatedVersion));

  if (alreadyHandled) return;
  if (updatedVersion.reportImagesJob && !claimJobCompletion(updatedVersion.reportImagesJob)) {
    return;
  }

  yield call(applyReportImagesSideEffects, updatedVersion, message.status);
}

function* handleProgressEvent(_data: string): Generator<unknown, void, unknown> {
  // No additional processing needed for progress events at this time
  yield;
}

function* handleNotificationEvent(data: string): Generator<unknown, void, unknown> {
  const notification = parseWebSocketData<NotificationResponse>(data, WebSocketEventType.NOTIFICATION);
  if (!notification) return;

  const { addNotification } = notificationsReducer.actions;
  yield put(addNotification(notification));
}

export function* handleEndLampSimilarityEvent(data: string): Generator<unknown, void, unknown> {
  const isValid = yield call(validateProjectContext);
  if (!isValid) return;

  const message = parseWebSocketData<EndLampSimilarityEvent>(data, WebSocketEventType.END_LAMP_SIMILARITY);
  if (!message) return;
  const updatedVersion: Version = message.version;

  const localVersion = (yield call(getLocalVersion, updatedVersion.id)) as Version | null;
  const alreadyHandled = isJobTerminal(localVersion?.lampSimilarityJob);

  yield put(updateActiveVersion(updatedVersion));

  if (alreadyHandled) return;
  if (updatedVersion.lampSimilarityJob && !claimJobCompletion(updatedVersion.lampSimilarityJob)) {
    return;
  }

  yield call(applyLampSimilaritySideEffects, updatedVersion, message.status);
}

function* handleCatalogueSyncFinishedEvent(data: string): Generator<unknown, void, unknown> {
  const message = parseWebSocketData<CatalogueSyncFinishedEvent>(data, WebSocketEventType.CATALOGUE_SYNC_FINISHED);
  if (!message) return;

  if (message.status === CatalogueSyncStatus.SUCCESS) {
    yield put(
      showToast({
        message: 'catalogue.syncFinishedSuccess',
        type: 'success',
      }),
    );
  } else {
    yield put(
      showToast({
        message: 'catalogue.syncFinishedError',
        type: 'error',
      }),
    );
  }
}

// Saga to handle WS subscription
function* handleSubscribeToWebSocket(): Generator<unknown, void, unknown> {
  clearClaimedJobCompletions();

  // Refresh the access token before opening the WebSocket
  const tokenReady = (yield call(ensureFreshAccessToken)) as boolean;
  if (!tokenReady) {
    console.error('Could not refresh access token; aborting WebSocket subscription');
    return;
  }

  const { channel, forceReconnect } = (yield call(createWebSocketChannel)) as WebSocketChannelHandle;
  const catchUpTask = (yield fork(jobStatusCatchUpLoop)) as Task;

  try {
    while (true) {
      const { message, timeout } = (yield race({
        message: take(channel),
        timeout: delay(HEARTBEAT_TIMEOUT_MS),
      })) as {
        message?: ChannelMessage;
        timeout?: true;
      };

      if (timeout) {
        console.warn('WebSocket heartbeat timeout; forcing reconnect');
        forceReconnect();
        continue;
      }

      if (!message) {
        continue;
      }

      // Handle synthetic reconnect event
      if (message.type === WS_RECONNECTED) {
        yield call(syncAppState);
        continue;
      }

      const wsMessage = message as WebSocketMessage;

      switch (message.type) {
        case WebSocketEventType.HEARTBEAT:
          noop();
          break;
        case WebSocketEventType.END_AUTO_POSITIONING:
          yield call(handleEndAutoPositioningEvent, wsMessage.data);
          break;
        case WebSocketEventType.END_CALCULATION:
          yield call(handleEndCalculationEvent, wsMessage.data);
          break;
        case WebSocketEventType.END_RENDER_BAKING:
          yield call(handleEndRenderBakingEvent, wsMessage.data);
          break;
        case WebSocketEventType.END_REPORT_IMAGES:
          yield call(handleEndReportImagesEvent, wsMessage.data);
          break;
        case WebSocketEventType.PROGRESS:
          yield call(handleProgressEvent, wsMessage.data);
          break;
        case WebSocketEventType.NOTIFICATION:
          yield call(handleNotificationEvent, wsMessage.data);
          break;
        case WebSocketEventType.END_LAMP_SIMILARITY:
          yield call(handleEndLampSimilarityEvent, wsMessage.data);
          break;
        case WebSocketEventType.JOB_STARTED:
          yield call(handleJobStartedEvent, wsMessage.data);
          break;
        case WebSocketEventType.CATALOGUE_SYNC_FINISHED:
          playNotificationSound();
          yield call(handleCatalogueSyncFinishedEvent, wsMessage.data);
          break;
        default:
          console.warn('Unknown event type:', wsMessage.type);
      }
    }
  } finally {
    yield cancel(catchUpTask);
    clearClaimedJobCompletions();
    channel.close();
  }
}

export default function* webSocketSaga(): SagaIterator {
  let subscriptionTask: Task | null = null;

  while (true) {
    const action = (yield take([SUBSCRIBE_TO_WEBSOCKET_ACTION, UNSUBSCRIBE_FROM_WEBSOCKET_ACTION])) as {
      type: string;
    };

    if (action.type === SUBSCRIBE_TO_WEBSOCKET_ACTION) {
      if (subscriptionTask) {
        yield cancel(subscriptionTask);
      }
      subscriptionTask = (yield fork(handleSubscribeToWebSocket)) as Task;
    }

    if (action.type === UNSUBSCRIBE_FROM_WEBSOCKET_ACTION && subscriptionTask) {
      yield cancel(subscriptionTask);
      subscriptionTask = null;
    }
  }
}
